import { computed, DestroyRef, inject, Injectable, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { TariffWarningMessage } from '@products/models/tariff.model';
import {
  PromoCodeWarningMessage,
  VoucherType,
  VoucherValidationState,
  VoucherWarningMessage,
  VoucherWarningMessageState
} from '@products/models/voucher.model';
import { StatusBarService } from '@shared/status-bar/status-bar.service';
import { getIsTranslationLoaded } from '@store/translation/translation.selectors';
import { combineLatest, first, iif, map, Observable, of, pipe, switchMap, tap } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class VoucherWarningMessageService {
  private readonly destroyRef = inject(DestroyRef);
  private readonly store = inject(Store);
  private readonly statusBarService = inject(StatusBarService);
  private readonly translateService = inject(TranslateService);

  private invalidVoucherWarningStatusMessage = '';

  #voucherWarningMessageState = signal<VoucherWarningMessageState>({
    ticketLimitPerUserAccount: null,
    ticketLimitWarningMessage: null,
    voucherWarningMessage: null,
    invalidPromoCodeWarningMessage: null,
    invalidPromoCodes: null,
    notCombinablePromoCodesWarningMessage: null,
    notCombinablePromoCodes: null
  });

  voucherWarningMessageState = computed(this.#voucherWarningMessageState);

  initState(voucherWarningMessageState: VoucherWarningMessageState) {
    this.#voucherWarningMessageState.set(voucherWarningMessageState);
  }

  updateState(updateVoucherWarningMessageState: Partial<VoucherWarningMessageState>) {
    this.#voucherWarningMessageState.update((voucherWarningMessageState) => ({
      ...voucherWarningMessageState,
      ...updateVoucherWarningMessageState
    }));
  }

  initVoucherStatusMessageTranslationSubscription() {
    return this.getWarningMessageTranslation$('response.voucherNotValid')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(
        (translatedInvalidVoucherWarning) => (this.invalidVoucherWarningStatusMessage = translatedInvalidVoucherWarning)
      );
  }

  showInvalidVoucherAvailableTariffsWarningMessagePipe(initialNumberOfAvailableTariffs: number) {
    return pipe(
      switchMap((currentNumberOfAvailableTariffs: number) =>
        iif(
          () => currentNumberOfAvailableTariffs > 0,
          of(''),
          this.getInvalidVoucherAvailableTariffsWarningMessageTranslation$(initialNumberOfAvailableTariffs)
        ).pipe(
          map((availableTariffsLimitWarningMessage) => ({
            currentNumberOfAvailableTariffs,
            availableTariffsLimitWarningMessage
          }))
        )
      ),
      first(),
      tap(({ availableTariffsLimitWarningMessage }) => {
        if (availableTariffsLimitWarningMessage) {
          this.showInvalidVoucherWarningStatusMessage(availableTariffsLimitWarningMessage);
        }
      })
    );
  }

  showInvalidVoucherTypeWarningMessagePipe(voucherType: VoucherType, voucherValidationState: VoucherValidationState) {
    return pipe(
      switchMap((voucherTypeValidatedCount: number) =>
        iif(
          () => voucherTypeValidatedCount > 0,
          of(''),
          this.getInvalidVoucherTypeWarningMessageTranslation$(voucherType, voucherValidationState)
        ).pipe(
          map((voucherTypeLimitWarningMessage) => ({
            voucherTypeValidatedCount,
            voucherTypeLimitWarningMessage
          }))
        )
      ),
      first(),
      tap(({ voucherTypeLimitWarningMessage }) => {
        if (voucherTypeLimitWarningMessage) {
          this.showInvalidVoucherWarningStatusMessage(voucherTypeLimitWarningMessage);
        }
      })
    );
  }

  showInvalidVoucherWarningStatusMessage(invalidVoucherWarningStatusMessage?: string) {
    const warningStatusMessage = invalidVoucherWarningStatusMessage || this.invalidVoucherWarningStatusMessage;

    this.statusBarService.setStatus(warningStatusMessage, 'error');
  }

  setTicketLimitWarningMessage() {
    this.updateState({ ticketLimitWarningMessage: TariffWarningMessage.ERROR_TICKET_WARNING });
  }

  setTicketLimitPerUserAccountWarningMessage(ticketLimitPerUserAccount: number) {
    this.updateState({
      ticketLimitPerUserAccount,
      ticketLimitWarningMessage: TariffWarningMessage.TICKET_LIMIT_PER_USER_ACCOUNT_WARNING
    });
  }

  setVoucherAnonymousPriceTicketWarningMessage() {
    this.updateState({ voucherWarningMessage: VoucherWarningMessage.VOUCHER_ANONYMOUS_PRICED_TICKET_WARNING });
  }

  setRegularVoucherAfterAnonymousTicketWarningMessage() {
    this.updateState({
      voucherWarningMessage: VoucherWarningMessage.VOUCHER_REGULAR_AFTER_ANONYMOUS_TICKET_WARNING
    });
  }

  setInvalidPromoCodeWarningMessage(invalidPromoCodes: string) {
    this.updateState({
      invalidPromoCodeWarningMessage: PromoCodeWarningMessage.INVALID_PROMO_CODE_WARNING,
      invalidPromoCodes
    });
  }

  setNotCombinablePromoCodeWarningMessage(notCombinablePromoCodes: string) {
    this.updateState({
      notCombinablePromoCodesWarningMessage: PromoCodeWarningMessage.NOT_COMBINABLE_PROMO_CODES_WARNING,
      notCombinablePromoCodes
    });
  }

  clearVoucherWarningMessage() {
    this.#voucherWarningMessageState.update((voucherWarningMessageState) => ({
      ...voucherWarningMessageState,
      voucherWarningMessage: ''
    }));
  }

  clearTicketLimitWarningMessage() {
    this.#voucherWarningMessageState.update((voucherWarningMessageState) => ({
      ...voucherWarningMessageState,
      ticketLimitWarningMessage: ''
    }));
  }

  clearInvalidPromoCodeWarningMessage() {
    this.#voucherWarningMessageState.update((voucherWarningMessageState) => ({
      ...voucherWarningMessageState,
      invalidPromoCodeWarningMessage: '',
      invalidPromoCodes: ''
    }));
  }

  clearNotCombinablePromoCodeWarningMessage(clearNotCombinablePromoCodesWarningMessage = true) {
    if (!clearNotCombinablePromoCodesWarningMessage) {
      return;
    }

    this.#voucherWarningMessageState.update((voucherWarningMessageState) => ({
      ...voucherWarningMessageState,
      notCombinablePromoCodesWarningMessage: '',
      notCombinablePromoCodes: ''
    }));
  }

  private getInvalidVoucherTypeWarningMessageTranslation$(
    voucherType: VoucherType,
    voucherValidationState: VoucherValidationState
  ): Observable<string> {
    const { limitPromoCodes, limitLimitedPromoCodes } = voucherValidationState;

    return this.getWarningMessageTranslation$(TariffWarningMessage.VOUCHER_LIMITED_WARNING, {
      maxTicketLimit: voucherType === VoucherType.PromoCode ? limitPromoCodes : limitLimitedPromoCodes
    });
  }

  private getInvalidVoucherAvailableTariffsWarningMessageTranslation$(
    initialNumberOfAvailableTariffs: number
  ): Observable<string> {
    return this.getWarningMessageTranslation$(TariffWarningMessage.ERROR_TICKET_WARNING, {
      maxTicketLimit: initialNumberOfAvailableTariffs
    });
  }

  private getWarningMessageTranslation$(key: string | string[], params?: object): Observable<string> {
    return combineLatest({
      isTranslationLoaded: this.store.select(getIsTranslationLoaded),
      statusWarningMessage: this.translateService.stream(key, params)
    }).pipe(
      first(({ isTranslationLoaded, statusWarningMessage }) => isTranslationLoaded && statusWarningMessage),
      map(({ statusWarningMessage }) => statusWarningMessage)
    );
  }
}
